import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Mail, User, MessageSquare } from "lucide-react";
import AnimatedSection from "./AnimatedSection";

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const subject = `Contacto de ${formData.name} desde la web`;
      const body = `
Nombre: ${formData.name}
Email: ${formData.email}
Mensaje: ${formData.message}
      `;
      window.location.href = `mailto:ventas@sergen.pe?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

      toast({
        title: "Mensaje enviado",
        description: "Gracias por contactarnos. Te responderemos a la brevedad.",
      });
      setFormData({ name: "", email: "", message: "" });
    } catch {
      toast({
        title: "Error al enviar",
        description: "Ha ocurrido un error. Por favor, intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-[#f9f9f9] py-24">
      <div className="container mx-auto px-6">
        <AnimatedSection className="mb-16 text-center">
          <h2 className="mb-4 text-4xl font-extrabold text-gray-900">
            ¿Listo para optimizar tu consumo energético?
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-gray-600">
            Escríbenos a{" "}
            <span className="font-semibold text-[#F58A00]">
              info@sergen.pe
            </span>{" "}
            y descubre cómo podemos ayudarte a reducir costos y mejorar tu eficiencia energética.
          </p>
        </AnimatedSection>

        <div className="mx-auto max-w-2xl bg-white rounded-2xl shadow-md p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <label htmlFor="name" className="mb-2 block text-sm font-medium text-gray-800">
                  Nombre
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-gray-50 focus:ring-[#F58A00] focus:border-[#F58A00]"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-800">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-gray-50 focus:ring-[#F58A00] focus:border-[#F58A00]"
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="message" className="mb-2 block text-sm font-medium text-gray-800">
                Mensaje
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="pl-10 min-h-[150px] bg-gray-50 focus:ring-[#F58A00] focus:border-[#F58A00]"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full text-white bg-[#F58A00] hover:bg-[#e57f00] font-semibold text-lg py-6 rounded-xl shadow-md transition-all duration-200"
            >
              {isSubmitting ? "Enviando..." : "Enviar Mensaje"}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
